import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, CartesianGrid } from 'recharts';
import { MdDownload, MdLocalShipping, MdDescription, MdCardGiftcard, MdEvent, MdComputer, MdAccountBalanceWallet, MdTrendingUp, MdCheckCircle } from 'react-icons/md';
import api from '../services/api';
import { getCurrentUser } from '../services/authService';
import { useNavigate } from 'react-router-dom';
import ClientLayout from '../components/ClientLayout';

const BRAND = '#068BC9';
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const SERVICE_COLORS = ['#068BC9', '#8b5cf6', '#22c55e', '#f97316', '#ef4444'];
const INDIAN_CITIES = ['All Cities','Mumbai','Delhi','Bangalore','Hyderabad','Chennai','Kolkata','Pune','Ahmedabad','Jaipur','Surat','Lucknow','Kanpur','Nagpur','Indore','Bhopal','Patna','Vadodara','Ghaziabad','Ludhiana','Agra','Nashik','Faridabad','Meerut','Rajkot','Varanasi','Srinagar','Aurangabad','Dhanbad','Amritsar','Allahabad','Ranchi','Howrah','Coimbatore','Jabalpur','Gwalior','Vijayawada','Jodhpur','Madurai','Raipur','Kota','Guwahati','Chandigarh','Solapur','Hubli','Thiruvananthapuram','Mysuru','Tiruppur','Gurgaon','Noida','Secunderabad'];

function formatLakh(val) {
  if (val >= 100000) return String.fromCharCode(8377) + (val/100000).toFixed(1) + 'L';
  if (val >= 1000) return String.fromCharCode(8377) + (val/1000).toFixed(1) + 'K';
  return String.fromCharCode(8377) + parseFloat(val || 0).toFixed(0);
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{label}</p>
        <p style={{ fontSize: 14, fontWeight: 700, color: BRAND }}>{String.fromCharCode(8377)}{payload[0].value.toFixed(2)}L</p>
      </div>
    );
  }
  return null;
};

export default function RapidoAnalytics() {
  const user = getCurrentUser();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [invoices, setInvoices] = useState([]);
  const [walletData, setWalletData] = useState(null);
  const [cgOrders, setCgOrders] = useState([]);
  const [selectedCity, setSelectedCity] = useState('All Cities');
  const [companyCity, setCompanyCity] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [showCityPopup, setShowCityPopup] = useState(false);

  useEffect(() => {
    if (!user?.companyId) return;
    Promise.all([
      api.get(`/api/analytics/overview/${user.companyId}`),
      api.get(`/api/company-invoices/company/${user.companyId}`),
      api.get(`/api/corporate-giftings/company/${user.companyId}`).catch(() => null),
      api.get(`/api/wallet/balance/${user.companyId}`).catch(() => null),
      api.get(`/api/companies/${user.companyId}`).catch(() => null),
    ]).then(([analyticsRes, invoicesRes, cgRes, walletRes, companyRes]) => {
      setData(analyticsRes.data);
      setInvoices(invoicesRes.data);
      if (cgRes) setCgOrders(cgRes.data || []);
      if (walletRes) setWalletData(walletRes.data);
      if (companyRes) {
        const city = companyRes.data?.city || '';
        const name = companyRes.data?.name || '';
        setCompanyCity(city);
        setCompanyName(name);
        localStorage.setItem('companyCity', city);
        localStorage.setItem('companyName', name);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user?.companyId]);

  const cityMatches = selectedCity === "All Cities" || !companyCity || selectedCity === companyCity;

  const monthlyData = (cityMatches ? data?.monthlyTrend : [])?.map(m => ({
    name: MONTHS[(parseInt(m.month) || 1) - 1],
    spend: parseFloat(m.spend || 0) / 100000
  })) || [];

  const totalSpend = cityMatches ? (data?.totalSpend || 0) : 0;
  const serviceSpend = cityMatches ? (data?.serviceSpend || []) : [];
  const totalServiceSpend = serviceSpend.reduce((a, b) => a + parseFloat(b.spend || 0), 0);

  const SERVICES = [
    { label: 'Logistics', icon: <MdLocalShipping size={22}/>, path: '/client/logistics', color: BRAND, key: 'Logistics' },
    { label: 'Stamp Paper', icon: <MdDescription size={22}/>, path: '/stamp-paper', color: '#8b5cf6', key: 'Stamp Paper' },
    { label: 'Corporate Gifting', icon: <MdCardGiftcard size={22}/>, path: '/gifting', color: '#22c55e', key: 'Corporate Gifting' },
    { label: 'Event & Team Outing', icon: <MdEvent size={22}/>, path: '/events', color: '#f97316', key: 'Events' },
    { label: 'IT Solutions', icon: <MdComputer size={22}/>, path: '/it-solutions', color: '#ef4444', key: 'IT Solutions' },
  ];

  const statCards = [
    { label: 'Total Expenses', value: formatLakh(totalSpend), sub: 'FY 2026-27', icon: <MdAccountBalanceWallet size={20}/>, color: BRAND, bg: '#e0f2fe' },
    { label: 'Logistics Spend', value: formatLakh(parseFloat(serviceSpend.find(s=>s.service==='Logistics')?.spend||0)), sub: `${serviceSpend.find(s=>s.service==='Logistics')?.requests||0} shipments`, icon: <MdLocalShipping size={20}/>, color: '#068BC9', bg: '#e0f2fe' },
    { label: 'Corporate Gifting Spend', value: formatLakh(parseFloat(serviceSpend.find(s=>s.service==='Corporate Gifting')?.spend||0)), sub: `${serviceSpend.find(s=>s.service==='Corporate Gifting')?.requests||0} orders`, icon: <MdCardGiftcard size={20}/>, color: '#22c55e', bg: '#dcfce7' },
    { label: 'Stamp Paper Spend', value: formatLakh(parseFloat(serviceSpend.find(s=>s.service==='Stamp Paper')?.spend||0)), sub: `${serviceSpend.find(s=>s.service==='Stamp Paper')?.requests||0} orders`, icon: <MdDescription size={20}/>, color: '#8b5cf6', bg: '#ede9fe' },
    { label: 'Event Spend', value: formatLakh(parseFloat(serviceSpend.find(s=>s.service==='Events')?.spend||0)), sub: `${serviceSpend.find(s=>s.service==='Events')?.requests||0} events`, icon: <MdEvent size={20}/>, color: '#f97316', bg: '#ffedd5' },
    { label: 'IT Spend', value: formatLakh(parseFloat(serviceSpend.find(s=>s.service==='IT Solutions')?.spend||0)), sub: `${serviceSpend.find(s=>s.service==='IT Solutions')?.requests||0} tickets`, icon: <MdComputer size={20}/>, color: '#ef4444', bg: '#fee2e2' },
  ];

  const barData = serviceSpend.map((s, i) => ({
    name: s.service === 'Corporate Gifting' ? 'Gifting' : s.service === 'Stamp Paper' ? 'Stamp' : s.service,
    value: parseFloat(s.spend || 0) / 1000,
    color: SERVICE_COLORS[i]
  }));

  return (
    <ClientLayout>
      <div className="p-6 space-y-5">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Pan-India Analytics</h1>
          <p className="text-xs text-gray-400">Consolidated expense and service overview</p>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
            {['overview', 'services', 'reports'].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className="px-5 py-2 rounded-lg text-sm font-medium capitalize transition-all"
                style={{ background: activeTab === tab ? 'white' : 'transparent', color: activeTab === tab ? BRAND : '#6b7280', boxShadow: activeTab === tab ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
          {activeTab === 'overview' && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">City:</span>
              <select value={selectedCity} onChange={e => { const c = e.target.value; setSelectedCity(c); localStorage.setItem('selectedCity', c); if (c !== 'All Cities' && companyCity && c !== companyCity) setShowCityPopup(true); }}
                className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 text-gray-700 bg-white focus:outline-none">
                {INDIAN_CITIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && (
              <div className="space-y-4">
                <div className="grid grid-cols-4 gap-3">
                  {statCards.map((card, i) => (
                    <div key={i} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: card.bg, color: card.color }}>
                          {card.icon}
                        </div>
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: card.bg, color: card.color }}>FY 26-27</span>
                      </div>
                      <p className="text-2xl font-bold text-gray-800 mb-1">{card.value}</p>
                      <p className="text-xs text-gray-400">{card.label}</p>
                      <p className="text-xs text-gray-300 mt-1">{card.sub}</p>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-5 gap-4">
                  <div className="col-span-3 bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-semibold text-gray-800">Expense Overview</p>
                      <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-1.5 text-xs text-gray-600 cursor-pointer">
                        FY 2026-27 <span className="ml-1">▾</span>
                      </div>
                    </div>
                    {monthlyData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={240}>
                        <AreaChart data={monthlyData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                          <defs>
                            <linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.15}/>
                              <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.02}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} dy={8} />
                          <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={v => v + 'L'} width={35} />
                          <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#e2e8f0', strokeWidth: 1 }} />
                          <Area type="monotone" dataKey="spend" stroke="#3b82f6" strokeWidth={2.5} fill="url(#grad1)"
                            dot={false} activeDot={{ r: 5, fill: '#3b82f6', stroke: 'white', strokeWidth: 2 }} />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-56 text-gray-300 text-sm">No data for selected city</div>
                    )}
                  </div>

                  <div className="col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                    <p className="text-sm font-semibold text-gray-800 mb-1">By Service</p>
                    <p className="text-xs text-gray-400 mb-5">Share of {formatLakh(totalSpend)}</p>
                    <div className="space-y-4">
                      {serviceSpend.length > 0 ? serviceSpend.map((s, i) => {
                        const pct = totalServiceSpend > 0 ? (parseFloat(s.spend) / totalServiceSpend * 100) : 0;
                        return (
                          <div key={s.service}>
                            <div className="flex items-center justify-between mb-1.5">
                              <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{backgroundColor: SERVICE_COLORS[i]}}></span>
                                <p className="text-xs text-gray-600">{s.service === 'Corporate Gifting' ? 'Gifting' : s.service}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <p className="text-xs font-semibold text-gray-700">{formatLakh(parseFloat(s.spend||0))}</p>
                                <p className="text-xs text-gray-400 w-8 text-right">{pct.toFixed(0)}%</p>
                              </div>
                            </div>
                            <div className="h-1.5 rounded-full bg-gray-100">
                              <div className="h-1.5 rounded-full transition-all duration-500" style={{ width: pct + '%', backgroundColor: SERVICE_COLORS[i] }}></div>
                            </div>
                          </div>
                        );
                      }) : (
                        <div className="text-xs text-gray-300 text-center pt-8">No data for selected city</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'services' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-500">Click a service to view details and make requests.</p>
                <div className="grid grid-cols-2 gap-4">
                  {SERVICES.map(svc => {
                    const svcData = serviceSpend.find(s => s.service === svc.key);
                    return (
                      <div key={svc.label} onClick={() => navigate(svc.path)}
                        className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 cursor-pointer hover:shadow-md transition-all">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: svc.color + '15', color: svc.color }}>
                            {svc.icon}
                          </div>
                          <p className="font-semibold text-gray-800">{svc.label}</p>
                        </div>
                        <div className="flex gap-4">
                          <div>
                            <p className="text-xs text-gray-400">Spend</p>
                            <p className="text-sm font-bold text-gray-800">{formatLakh(parseFloat(svcData?.spend||0))}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-400">Requests</p>
                            <p className="text-sm font-bold text-gray-800">{svcData?.requests||0}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {activeTab === 'reports' && (
              cityMatches
                ? (
              <div className="space-y-5">
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <MdAccountBalanceWallet size={20} style={{ color: BRAND }} />
                    <p className="text-sm font-semibold text-gray-700">Wallet Summary</p>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs text-gray-400 mb-1">Total Recharged</p>
                      <p className="text-lg font-bold text-gray-800">{String.fromCharCode(8377)}{parseFloat(walletData?.totalRechargedAmount || 0).toLocaleString('en-IN', {minimumFractionDigits:2})}</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs text-gray-400 mb-1">Amount Used</p>
                      <p className="text-lg font-bold text-gray-800">{String.fromCharCode(8377)}{parseFloat(walletData?.walletUsedAmount || 0).toLocaleString('en-IN', {minimumFractionDigits:2})}</p>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-3">
                      <p className="text-xs text-gray-400 mb-1">Balance</p>
                      <p className="text-lg font-bold" style={{ color: BRAND }}>{String.fromCharCode(8377)}{parseFloat(walletData?.balanceAmount || 0).toLocaleString('en-IN', {minimumFractionDigits:2})}</p>
                    </div>
                  </div>
                </div>

                {cgOrders.filter(o => o.poFile).length > 0 && (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                    <p className="text-sm font-semibold text-gray-700 mb-4">Corporate Gifting — Purchase Orders</p>
                    <div className="space-y-3">
                      {cgOrders.filter(o => o.poFile).map(cg => (
                        <div key={cg.id} className="border border-gray-100 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <p className="text-xs font-semibold text-gray-700">{cg.serviceRequestId}</p>
                              {cg.poNumber && <p className="text-xs text-gray-400">PO: {cg.poNumber}</p>}
                            </div>
                            <a href={'https://api.prodiligix.com' + cg.poFile} target="_blank" rel="noreferrer"
                              className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg text-white" style={{backgroundColor: BRAND}}>
                              <MdDownload size={12}/> PO
                            </a>
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            <div className="bg-gray-50 rounded-lg p-2 text-center">
                              <p className="text-xs text-gray-400 mb-1">PO Amount</p>
                              <p className="text-sm font-bold text-gray-800">{String.fromCharCode(8377)}{parseFloat(cg.poAmount || 0).toLocaleString('en-IN', {minimumFractionDigits:2})}</p>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-2 text-center">
                              <p className="text-xs text-gray-400 mb-1">Payment Term</p>
                              <p className="text-sm font-bold text-gray-800">{cg.poValidity || '—'}</p>
                            </div>
                            <div className="bg-blue-50 rounded-lg p-2 text-center">
                              <p className="text-xs text-gray-400 mb-1">Balance</p>
                              <p className="text-sm font-bold" style={{color: BRAND}}>{String.fromCharCode(8377)}{(parseFloat(cg.poAmount || 0) - parseFloat(cg.poAmountUsed || 0)).toLocaleString('en-IN', {minimumFractionDigits:2})}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                  <p className="text-sm font-semibold text-gray-700 mb-4">Service Reports</p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'Logistics Report', tab: 'logistics', color: BRAND },
                      { label: 'Stamp Paper Report', tab: 'stamp', color: '#8b5cf6' },
                      { label: 'Corporate Gifting Report', tab: 'gifting', color: '#22c55e' },
                      { label: 'Events Report', tab: 'events', color: '#f97316' },
                      { label: 'IT Solutions Report', tab: 'it', color: '#ef4444' },
                    ].map(svc => (
                      <a key={svc.tab} href={'/client/reports?tab=' + svc.tab} target="_blank" rel="noreferrer"
                        className="flex items-center justify-between p-3 rounded-xl border border-gray-100 hover:shadow-sm transition-all">
                        <p className="text-sm font-medium text-gray-700">{svc.label}</p>
                        <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg text-white" style={{backgroundColor: svc.color}}>
                          <MdDownload size={12}/> Download
                        </span>
                      </a>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-700">Invoices ({invoices.length})</p>
                  </div>
                  {invoices.length === 0 ? (
                    <div className="p-8 text-center text-sm text-gray-400">No invoices yet</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>{['Invoice No.','Date','Description','Amount',''].map(h => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500">{h}</th>
                        ))}</tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {invoices.map(inv => (
                          <tr key={inv.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-xs font-medium" style={{ color: BRAND }}>{inv.invoiceNumber}</td>
                            <td className="px-4 py-3 text-xs text-gray-500">{inv.invoiceDate}</td>
                            <td className="px-4 py-3 text-xs text-gray-500">{inv.description||'—'}</td>
                            <td className="px-4 py-3 text-xs font-semibold text-gray-700">{String.fromCharCode(8377)}{parseFloat(inv.amount).toLocaleString('en-IN',{minimumFractionDigits:2})}</td>
                            <td className="px-4 py-3 text-xs">
                              {inv.filePath && (
                                <a href={'https://api.prodiligix.com' + inv.filePath} target="_blank" rel="noreferrer"
                                  className="flex items-center gap-1 px-2 py-1 rounded text-white text-xs w-fit" style={{ backgroundColor: BRAND }}>
                                  <MdDownload size={12}/> Download
                                </a>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
              )
                : (
                <div className="flex flex-col items-center justify-center h-64">
                  <p className="text-lg font-semibold text-gray-300">No data for selected city</p>
                  <p className="text-sm mt-1 text-gray-300">Select "All Cities" or your company city to view reports</p>
                </div>
              )
            )}
          </>
        )}
      </div>
      {showCityPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-sm w-full mx-4 text-center">
            <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
              <svg width="28" height="28" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="#068BC9" strokeWidth="2"/><path d="M12 8v4m0 4h.01" stroke="#068BC9" strokeWidth="2" strokeLinecap="round"/></svg>
            </div>
            <h2 className="text-lg font-bold text-gray-800 mb-2">{selectedCity} is not available</h2>
            <p className="text-sm text-gray-500 mb-6">{companyName || 'Your company'} does not operate in {selectedCity}. Please select a valid city.</p>
            <button onClick={() => { setSelectedCity(companyCity); localStorage.setItem('selectedCity', companyCity); setShowCityPopup(false); }}
              className="px-6 py-2 rounded-xl text-white text-sm font-medium" style={{ backgroundColor: '#068BC9' }}>
              Got it
            </button>
          </div>
        </div>
      )}
    </ClientLayout>
  );
}
